import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { type RefObject, useEffect, useRef, useState } from "react";
import { QuoteMatchMap, QuoteMatcher } from "../gradeValidationHelpers.js";
import type { CallPosition } from "../teammateAccountCallOutlineNavigation.js";
import { scrollToFragmentElement } from "./snippetSharingHelpers.js";
import type { FocusedSnippetState } from "./useTranscriptSnippetSharing.js";

/**
 * Focuses a quote in the open transcript: {@link QuoteMatcher} fuzzy-matches
 * the quoted text against the recordings, the audio player seeks, and the
 * transcript scrolls to the matched fragment — the UX of a shared snippet
 * link.
 *
 * Two callers drive it. A `?quote=` link focuses once on mount; the returned
 * `focusQuoteText` answers a click on a citation in a surface already showing
 * this interaction, which has no navigation to read a param from.
 */
export function useTranscriptQuoteFromUrl(params: {
  containerRef: RefObject<HTMLElement | null>;
  recordings: readonly AccountGradingRecording[];
  seek: (sourceKey: string, time: number) => void;
  scrollToCallPosition?: (position: CallPosition) => void;
}) {
  const { containerRef, recordings, seek, scrollToCallPosition } = params;

  const [activeSnippet, setActiveSnippet] = useState<
    FocusedSnippetState | undefined
  >();

  const focusQuoteText = useEffectEvent(
    (options: {
      quote: string;
      /**
       * Resolves the quote within one call rather than taking the first global
       * match. A shared snippet carries the recording it came from; a citation
       * in the chat does not.
       */
      recordingId?: string | undefined;
    }) => {
      const transcriptQuote = options.quote;
      if (!transcriptQuote) return;

      const quoteId = "transcript_quote";
      const { recordingId } = options;
      const scopedRecordings = recordingId
        ? recordings.filter((recording) => recording.id === recordingId)
        : recordings;
      const matcher = QuoteMatcher.new(scopedRecordings);
      const matches = matcher.findMatches([
        {
          type: "quote",
          id: quoteId,
          short: transcriptQuote,
          long: transcriptQuote,
        },
      ]);

      const quoteMatches = QuoteMatchMap.findByQuote(matches, quoteId);
      if (!quoteMatches || quoteMatches.length === 0) return;

      const firstMatch = quoteMatches[0];
      if (!firstMatch) return;
      const recording = recordings.find((r) => r.id === firstMatch.recordingId);
      if (!recording?.transcript) return;

      const fragment =
        recording.transcript.fragments[firstMatch.recordingFragmentIndex];
      if (!fragment) return;

      seek(firstMatch.recordingId, fragment.start_time / 1000);

      scrollToCallPosition?.({
        recordingId: firstMatch.recordingId,
        timestamp: fragment.start_time,
      });

      const container = containerRef.current;
      if (container) {
        scrollToFragmentElement(
          container,
          firstMatch.recordingId,
          firstMatch.recordingFragmentIndex,
        );
      }

      setActiveSnippet({
        recordingId: firstMatch.recordingId,
        startFragmentIndex: firstMatch.recordingFragmentIndex,
        endFragmentIndex: firstMatch.recordingFragmentIndex,
        startCharOffset: firstMatch.inFragmentIndex,
        endCharOffset: firstMatch.inFragmentIndex + firstMatch.length,
      });
    },
  );

  const handleTranscriptQuote = useEffectEvent(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const quote = searchParams.get("quote");
    if (!quote) return;

    focusQuoteText({
      quote,
      recordingId: searchParams.get("recordingId") ?? undefined,
    });
  });

  const didHandleTranscriptQuoteRef = useRef(false);
  useEffect(() => {
    // If we already did this, don't do it again.
    if (didHandleTranscriptQuoteRef.current) {
      return;
    }

    // If recordings haven't loaded yet, wait until they have.
    if (recordings.length === 0) {
      return;
    }

    try {
      handleTranscriptQuote();
    } finally {
      didHandleTranscriptQuoteRef.current = true;
    }
  }, [recordings, handleTranscriptQuote]);

  return { activeSnippet, focusQuoteText };
}
